/**
 * SceneManager — monta renderer, cena, câmera aérea, luzes, chão e o
 * pipeline de pós-processamento (bloom). Expõe um registro de "updaters"
 * chamados a cada frame e um loop de render com EffectComposer.
 */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { FXAAShader } from "three/addons/shaders/FXAAShader.js";

/**
 * FocusOutputPass — focus blur + tone mapping + conversão de espaço de cor NUM
 * PASSE SÓ.
 *
 * Focus blur (profundidade de campo "fake"): mantém o miolo da cidade nítido e
 * borra progressivamente as bordas (florestas), via máscara elíptica. O blur
 * começa fraco na borda da cidade e fica mais forte quanto mais externo.
 *
 * Por que fundido no OutputPass: os dois eram passes fullscreen SEGUIDOS, cada um
 * lendo e escrevendo um render target inteiro. Como o blur roda em espaço LINEAR
 * (antes do tone mapping) nos dois arranjos, colher as 8 amostras dentro do
 * próprio OutputPass dá exatamente o mesmo resultado — e economiza um passe
 * fullscreen + um ping-pong de render target por frame (a leitura intermediária
 * em half-float some, então a precisão só melhora).
 *
 * A herança preserva TODA a lógica do three (defines de tone mapping/color space
 * recalculados por frame conforme o renderer); aqui só injetamos a coleta das
 * amostras no lugar da leitura simples da textura.
 *
 * CUSTO REAL: o `if (blur <= 0.002) return base;` ja faz o miolo da tela sair com
 * UMA leitura de textura. Medido sobre a mascara eliptica atual, so 31,8% dos
 * pixels chegam a fazer as amostras extras — o "ignorar o calculo no centro" ja
 * esta em vigor. `setFocusRings(1)` corta essas amostras de 8 para 4 no perfil
 * fraco.
 */
const FOCUS_UNIFORMS = /* glsl */ `
    uniform vec2 uResolution;
    uniform vec2 uCenter;
    uniform vec2 uEllipse;
    uniform float uInner;
    uniform float uOuter;
    uniform float uMaxRadius;
    uniform float uRings;

    vec4 focusSample(vec2 uv) {
      vec2 p = uv - uCenter;
      p.x *= uEllipse.x;
      p.y *= uEllipse.y;
      float dist = length(p);
      float blur = smoothstep(uInner, uOuter, dist);

      vec4 base = texture2D(tDiffuse, uv);
      if (blur <= 0.002) return base;

      // disco de 8 amostras (2 anéis × 4) — leve e suficiente para a periferia
      float radius = blur * uMaxRadius;
      vec2 asp = vec2(1.0, uResolution.x / uResolution.y);
      vec3 sum = base.rgb;
      float total = 1.0;
      // uRings=2 -> 8 amostras (2 aneis x 4); uRings=1 -> 4 amostras (perfil fraco).
      // Limite do laco constante + break: GLSL ES 1.0 exige contagem fixa, mas o
      // break permite mudar em tempo de execucao sem recompilar o programa.
      for (int r = 0; r < 2; r++) {
        if (float(r) >= uRings) break;
        float rf = (float(r) + 1.0) / uRings;
        float rot = float(r) * 0.785;
        for (int s = 0; s < 4; s++) {
          float a = (float(s) + 0.5) / 4.0 * 6.2831853 + rot;
          vec2 off = vec2(cos(a), sin(a)) * radius * rf * asp;
          sum += texture2D(tDiffuse, uv + off).rgb;
          total += 1.0;
        }
      }
      return vec4(sum / total, base.a);
    }

    varying vec2 vUv;`;

class FocusOutputPass extends OutputPass {
    constructor() {
        super();
        Object.assign(this.uniforms, {
            uResolution: { value: new THREE.Vector2(1, 1) },
            uCenter: { value: new THREE.Vector2(0.5, 0.5) },
            uEllipse: { value: new THREE.Vector2(0.95, 1.12) },
            uInner: { value: 0.47 }, // ~5% mais para dentro (laterais/topo/rodapé)
            uOuter: { value: 0.9 },
            uMaxRadius: { value: 0.02 },
            uRings: { value: 2 }, // 2 aneis = 8 amostras (padrao); 1 = 4 amostras
        });
        // injeta os uniforms + a função de coleta e troca a leitura simples por ela.
        // Se o three mudar o texto do OutputShader, o replace falha em silêncio e a
        // imagem perderia o blur — por isso a checagem explícita abaixo.
        const src = this.material.fragmentShader;
        const patched = src
            .replace('varying vec2 vUv;', FOCUS_UNIFORMS)
            .replace('texture2D( tDiffuse, vUv )', 'focusSample( vUv )');
        if (patched === src || patched.includes('gl_FragColor = texture2D')) {
            console.error('[FocusOutputPass] OutputShader mudou de forma — focus blur NÃO aplicado');
        }
        this.material.fragmentShader = patched;
        this.material.needsUpdate = true;
    }
}

import { COLORS, WORLD, CAMERA, BLOOM, GRID_FIT } from "../config.js";
import { createCityTexture } from "../world/CityTexture.js";
// escada + decisão da resolução automática (lógica pura, testável — ver autoRes.js)
import { AutoResController, AUTO_LADDER } from './autoRes.js';
import { BOOT_QUALITY, IS_ULTRA, TEX_TIER, resolvePixelRatio } from './qualityLevel.js';
// sondas por subsistema (custo ~zero desligadas — ver perfProbes.js)
import { probes, P } from '../perfProbes.js';

// temporários reaproveitados (sem alocar por frame na câmera dinâmica)
const _UP = new THREE.Vector3(0, 1, 0);
const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _v5 = new THREE.Vector3();
const _size = new THREE.Vector2();
// Cores-base da iluminação ambiente, já em espaço LINEAR (o THREE.Color converte de
// sRGB no construtor). _FILL_BASE é a cor da antiga luz de preenchimento direcional,
// hoje dobrada no ambiente — ver _ambientRGB.
const _AMB_BASE = new THREE.Color(0x5c8070);
const _FILL_BASE = new THREE.Color(0x2e7d5b);
const FILL_FOLD = 0.5; // dot(N,L) médio da luz de preenchimento na área visível

/**
 * MODOS DE ANTIALIASING. `antialias` é fixado na CRIAÇÃO do contexto WebGL, então
 * trocar de modo exige recarregar — por isso ele vem de `?aa=` ou do localStorage,
 * e não de um seletor que aplica na hora.
 *
 * Por que o padrão é 'smaa' (sem MSAA): o EffectComposer desenha a imagem final no
 * canvas como um QUAD DE TELA CHEIA. Um quad não tem aresta interna, e MSAA só
 * suaviza aresta de geometria — então o backbuffer multiamostrado do
 * `antialias: true` não melhora nada da cena e ainda assim é alocado e resolvido
 * todo quadro: a 1920×1080 com pixelRatio 1.0 são 31,6 MB (contra 7,9 MB) mais um
 * resolve por quadro, em memória compartilhada com a CPU numa GPU integrada.
 *
 * A ÚNICA geometria que ainda vai direto para o canvas é a cena de sobreposição
 * (rótulos e linhas conectoras). Os rótulos são quads com borda por alfa de
 * textura — MSAA não faz diferença neles. As LINHAS são cilindros de ~0,4 px de
 * largura: sem MSAA elas podem cintilar. É o ponto a olhar na TV ao comparar os
 * modos.
 */
export const AA_MODES = {
    'msaa+smaa': { msaa: true, smaa: true, fxaa: false },
    'smaa': { msaa: false, smaa: true, fxaa: false },
    'msaa': { msaa: true, smaa: false, fxaa: false },
    'fxaa': { msaa: false, smaa: false, fxaa: true },
    'none': { msaa: false, smaa: false, fxaa: false },
};
const AA_DEFAULT = 'smaa';

/**
 * PERFIS DE QUALIDADE. Só mexem no que domina o custo de GPU: a RESOLUÇÃO INTERNA
 * (todos os ~6 passes de tela cheia custam proporcionalmente a ela), a escala do
 * BLOOM e o nº de amostras do focus blur. Cor, sombra, geometria e o HUD não mudam
 * — o HUD é DOM/CSS e sempre desenha na resolução nativa da tela, independente do
 * pixelRatio do renderer.
 *
 * Referência medida a 1920×1080: pixelRatio 1.0 = 2,07M px por passe; 0,9 = 1,68M;
 * 0,85 = 1,50M; 0,75 = 1,17M (56% de 1.0).
 */
/**
 * O bloom é o passe mais caro (pirâmide de blur multi-nível) e o custo é
 * proporcional à RESOLUÇÃO do passe (`bloom` = escala sobre o canvas). O visual
 * do perfil ALTA é a referência e não muda (0.5); os perfis baratos descem mais:
 * o brilho é borrado por natureza, então meia-resolução a menos no passe custa
 * pouco de visual e corta ~30% do custo dele. `aa` = antialiasing PADRÃO do
 * perfil (SMAA são 3 passes de tela cheia; FXAA é 1 — nos perfis baratos o FXAA
 * é o padrão), aplicado SÓ se o usuário nunca escolheu um modo à mão no painel
 * (a escolha explícita ?aa=/localStorage sempre vence — ver setQuality).
 */
/**
 * ULTRA (acima de "alta"): o único perfil cujo `pr` é um ALVO e não um teto —
 * `resolvePixelRatio` (qualityLevel.js) lê `pr > 1` como "nunca abaixo da tela
 * física, e acima dela quando couber no orçamento". É o que resolve, de uma vez,
 * o serrilhado de silhueta dos prédios, o de textura, o das linhas finas E o do
 * OVERLAY dos nomes — que roda fora do composer e nenhum passe de AA alcança.
 *
 * O que ele NÃO mexe, de propósito:
 *   • bloom continua 0.5 — `bloom.setSize` trabalha em px CSS e ignora o
 *     pixelRatio, então em Ultra o passe sai de graça; subir para 1.0
 *     quadruplicaria o passe mais caro do pipeline sem tirar um serrilhado
 *     sequer (pior: um brilho mais nítido EVIDENCIA a escadinha das bordas).
 *   • rings continua 2 — o laço do focus blur tem o limite LITERAL 2 e o raio é
 *     (r+1)/uRings: com 3 ele faria as MESMAS 8 amostras com raios 1/3 e 2/3,
 *     ENCOLHENDO o desfoque de periferia. É uma regressão que nenhum harness vê.
 *   • aa continua 'smaa' — 'msaa+smaa' exigiria recarregar (o `antialias` é
 *     fixado na criação do contexto) e o MSAA do canvas não suaviza a cena, só o
 *     overlay, que o supersampling já cobre.
 *
 * O resto do Ultra (densidade de textura, sombra, anisotropia, copa dos
 * arbustos) mora fora dos 4 campos: ULTRA_EXTRA aqui embaixo e TEX_TIER em
 * qualityLevel.js. Manter a LINHA com exatamente estes 4 campos é contrato do
 * scripts/postFxHarness.mjs.
 */
const QUALITY = {
    ultra: { pr: 2.0, bloom: 0.5, rings: 2, aa: 'smaa' },
    alta: { pr: 1.0, bloom: 0.5, rings: 2, aa: 'smaa' },
    equilibrada: { pr: 0.9, bloom: 0.42, rings: 2, aa: 'smaa' },
    desempenho: { pr: 0.75, bloom: 0.32, rings: 1, aa: 'fxaa' },
};

/**
 * SOMBRA por perfil. O mapa é renderizado UMA VEZ e congelado
 * (shadowMap.autoUpdate = false), então subir a resolução custa VRAM e um único
 * render — ZERO por quadro. Por isso ela pode ser generosa em Ultra.
 *
 *   map  1024 sobre uma caixa de 240 u = 23,4 cm por texel = 1,9 px em 1080p,
 *        mas 3,8 px em 4K: o texel é fixo em MUNDO e o pixel encolhe, então esse
 *        serrilhado PIORA quanto maior a tela. 2048 devolve o 1,9 px em 4K.
 *        Não subimos a 4096: em PCFSoftShadowMap a penumbra é sempre ~1 texel,
 *        então mais resolução deixa a sombra mais NÍTIDA, não mais macia — a 4096
 *        ela vira recorte duro, o oposto do "suavizar" que se quer no telão.
 *   s    120 CORTA a cidade 6×4 (Mexican Town precisa de ~137) e deixa uma linha
 *        reta onde as sombras acabam. 140 cobre todas as cidades. `s` e `map`
 *        sobem JUNTOS — `s` sozinho só piora o texel.
 *   bias em profundidade NORMALIZADA: os -0.0004 de 1024 equivalem a 0,72 texel;
 *        a 2048 o mesmo número vira 1,4 texel e a sombra descola da base das
 *        paredes (peter-panning). Escala por (1024/map), com normalBias de ~1
 *        texel para compensar sem trazer acne.
 */
const SHADOW = {
    base: { map: 1024, s: 120, bias: -0.0004, normalBias: 0.02 },
    ultra: { map: 2048, s: 140, bias: -0.0002, normalBias: 0.05 },
};

export class SceneManager {
    constructor(container) {
        this.container = container;
        this.updaters = new Set();
        this.clock = new THREE.Clock();
        // TETO DE FPS (telão de evento: ~30 fps dá folga térmica numa projeção de 8h
        // e metade do trabalho de CPU/GPU; o bloom continua ligado). 0 = sem teto.
        this.fpsCap = 0;
        this._contextLost = false;

        this._initRenderer();
        this._initScene();
        this._initCamera();
        this._initControls();
        this._initLights();
        this._initGround();
        this._initPostFX();
        this._initContextRecovery(); // sobrevive à perda de contexto WebGL (kiosk 8h)

        window.addEventListener("resize", () => this._onResize());
    }

    _initRenderer() {
        this.aaMode = readAAMode();
        // modo com que o CONTEXTO foi criado: `antialias` não muda em runtime, então
        // é contra este valor que se decide se uma troca exige recarregar
        this._bootAA = this.aaMode;
        this.renderer = new THREE.WebGLRenderer({
            // ver AA_MODES: com o EffectComposer o MSAA do canvas normalmente não
            // melhora nada e custa um backbuffer 4× maior + resolve por quadro
            antialias: AA_MODES[this.aaMode].msaa,
            powerPreference: "high-performance",
        });
        // Resolução interna: 1.0 em Full HD é o ponto de partida (ver QUALITY). O
        // teto vira o nível escolhido nas Configurações, ou a escada automática.
        // O nível de BOOT já vale aqui (?q=/localStorage — ver qualityLevel.js):
        // em Ultra o pixelRatio precisa estar certo já na PRIMEIRA alocação, senão
        // a troca posterior realocaria todos os alvos do composer num quadro só.
        this.quality = QUALITY[BOOT_QUALITY] ? BOOT_QUALITY : "alta";
        this.maxPixelRatio = QUALITY[this.quality].pr;
        // ANISOTROPIA máxima da GPU (tipicamente 16). É o filtro que salva
        // superfície INCLINADA, e a câmera é isométrica a ~50° — chão, calçada,
        // asfalto e telhado são rasantes o quadro inteiro. Aplicada em Ultra por
        // uma passada única em main.js; aqui só descobrimos o limite do driver.
        this.maxAniso = this.renderer.capabilities.getMaxAnisotropy();
        // O tier de textura do Ultra (chão a 14 px/unidade = 4620×4480) é escolhido
        // antes de existir GPU, ao contrário do backbuffer, que _targetPixelRatio
        // clampa por maxTextureSize. Numa GPU com teto abaixo disso o browser
        // rejeita a textura e o chão some/embaralha SEM erro nenhum — então pelo
        // menos o console diz o que houve. Na prática é inalcançável (toda placa
        // capaz de rodar o Ultra reporta 8192+), e é por isso que a saída é um
        // aviso e não um rebaixamento automático: rebaixar exigiria repintar o
        // canvas do chão, e essa decisão já passou.
        const ladoChao = Math.max(WORLD.width, WORLD.depth) * TEX_TIER.groundPx;
        if (ladoChao > (this.renderer.capabilities.maxTextureSize || 8192)) {
            console.error(
                `[ultra] a textura do chão (${ladoChao}px de lado) passa do maxTextureSize ` +
                `da GPU (${this.renderer.capabilities.maxTextureSize}) — use ?q=alta nesta máquina`
            );
        }
        // Resolução AUTOMÁTICA (desligada por padrão): desce a escada quando o
        // quadro não cabe no orçamento e sobe quando sobra folga. A decisão é do
        // AutoResController (autoRes.js) — medida por tempo OCUPADO do quadro, com
        // o fpsCap respeitado como orçamento intencional; cada degrau aplica o
        // perfil completo (resolução + bloom + focus blur) por _applyProfile.
        this.autoRes = new AutoResController({ targetFps: 30 });
        this.renderer.setPixelRatio(this._targetPixelRatio());
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.38;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        // Os AVATARES não entram neste shadow map — usam "blob shadows" (manchas macias
        // que os seguem, ver BlobShadows). Sobram só prédios/árvores/postes, que são
        // ESTÁTICOS: renderizamos o mapa UMA VEZ e congelamos (autoUpdate=false). Assim
        // não há custo de sombra por frame e nada "trava" ao mover a câmera/avatares.
        this.renderer.shadowMap.autoUpdate = false;
        this.renderer.shadowMap.needsUpdate = true;
        this.container.appendChild(this.renderer.domElement);
    }

    _initScene() {
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(COLORS.sky);
        // fog escalado pelo FATOR DA GRADE (config.GRID_FIT): numa cidade maior a
        // câmera recua na mesma proporção, então o fog acompanha p/ o degradê cair
        // sobre a cidade igual ao da grade 5×3 (fator 1 = valores originais).
        this.scene.fog = new THREE.Fog(COLORS.fog, 300 * GRID_FIT, 620 * GRID_FIT);
        /**
         * CENA DE SOBREPOSIÇÃO — desenhada DEPOIS de todo o pós-processamento,
         * direto no buffer final. É onde vivem os rótulos dos jogadores.
         *
         * Por quê: dentro da cena principal eles atravessavam bloom, focus blur e
         * SMAA. O texto quase branco passa do limiar do bloom (luminância 0,93
         * contra 0,86) e ganhava um halo; e o focus blur, que borra a periferia da
         * tela de propósito, deixava os nomes das bordas moles — justamente onde
         * mais se precisa lê-los num telão. Aqui eles saem chapados e nítidos,
         * exatamente com as cores em que foram desenhados.
         *
         * O custo é praticamente o mesmo: é a mesma geometria, só num momento
         * diferente do quadro (nenhum passe de tela cheia a mais).
         */
        this.overlay = new THREE.Scene();
        // valores-base do fog (o mobile recua a câmera e empurra o fog junto — ver
        // _applyAspectFraming; no desktop o fator é 1 e estes ficam intactos)
        this._baseFogNear = this.scene.fog.near;
        this._baseFogFar = this.scene.fog.far;
    }

    _initCamera() {
        this.camera = new THREE.PerspectiveCamera(CAMERA.fov, window.innerWidth / window.innerHeight, 0.5, 2000);
        this.camera.position.set(...CAMERA.position);
    }

    _initControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.target.set(...CAMERA.target);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.06;
        // Modo manual = CÂMERA LIVRE (usada com a câmera dinâmica DESLIGADA, p/ enquadrar
        // screenshots de alta resolução): órbita + zoom + PAN + teclado, com limites
        // folgados (CAMERA.free). A câmera dinâmica ignora tudo isto (dirige a câmera
        // direto), então liberar aqui não interfere no modo automático.
        const F = CAMERA.free || CAMERA;
        this.controls.minPolarAngle = F.minPolar;
        this.controls.maxPolarAngle = F.maxPolar;
        this.controls.minDistance = F.minDistance;
        this.controls.maxDistance = F.maxDistance;
        this.controls.enablePan = true;
        this.controls.screenSpacePanning = true; // pan move p/ os lados e sobe/desce NA TELA
        this.controls.panSpeed = 0.9;
        this.controls.update();

        // Câmera dinâmica (LIGADA por padrão): ENQUADRA TODOS os jogadores. A vista
        // padrão (posição/alvo de carregamento) é a abertura/posição MÁXIMA — nunca
        // afasta além dela. No zoom-in pode girar até ~10° (dinamismo); ao voltar p/
        // a vista padrão a rotação volta a zero.
        this.dynamicCam = true;
        this.controls.enabled = false; // no modo dinâmico não há orbitar/zoom manual
        this._t0 = new THREE.Vector3(...CAMERA.target);
        this._p0 = new THREE.Vector3(...CAMERA.position);
        this._camDir = this._p0.clone().sub(this._t0).normalize();
        // distância MÁX da câmera dinâmica (vista aberta). No DESKTOP é a distância de
        // carregamento (config); em telas estreitas/baixas (mobile) recua mais — senão
        // a câmera fica "presa" no zoom máximo e parece estática. Ver _applyAspectFraming.
        this._baseDistMax = this._p0.distanceTo(this._t0);
        this._camDistMax = this._baseDistMax;
        // câmera auxiliar (só p/ medir o enquadramento) + buffers reaproveitados
        this._fitCam = new THREE.PerspectiveCamera(CAMERA.fov, this.camera.aspect, 0.5, 2000);
        this._fitPts = Array.from({ length: 8 }, () => new THREE.Vector3());
        this._fitTmp = new THREE.Vector3();
        this._fitAim = new THREE.Vector3(); // alvo resolvido pelo _fitPlacement
        this._dynTarget = this._t0.clone();
        this._dynRot = 0;
        this._applyAspectFraming(); // ajusta _camDistMax/fog à proporção da tela (mobile)
        this._dynDist = this._camDistMax;
        // posiciona na vista aberta correta p/ ESTA tela (no desktop = config, sem mudança)
        this.camera.position.copy(this._t0).addScaledVector(this._camDir, this._camDistMax);
        this.camera.lookAt(this._t0);
        this.controls.target.copy(this._t0);

        // Câmera de PERSEGUIÇÃO ("visão do jogador", estilo GTA) — DESLIGADA por padrão.
        // Fica ATRÁS do avatar no sentido do deslocamento, olhando um pouco à frente dele.
        // Estado persistente (posição/alvo/rumo suavizados entre frames). Ver
        // updateFollowCamera(). Mutuamente exclusiva com a câmera dinâmica.
        this.followCam = false;
        this._followYaw = null;                       // rumo suavizado (rad)
        this._followPos = this.camera.position.clone(); // posição suavizada (parte da vista aérea)
        this._followAim = this._t0.clone();            // alvo suavizado (p/ onde olha)
    }

    /** Liga/desliga a câmera de perseguição (visão do jogador). Ao ligar, desativa a
     *  câmera dinâmica e a livre (a câmera passa a ser dirigida por updateFollowCamera). */
    setFollowCamera(on) {
        this.followCam = !!on;
        if (this.followCam) {
            this.dynamicCam = false;
            this.controls.enabled = false;
            this._followPos.copy(this.camera.position); // parte de onde a câmera está agora
            this._followAim.copy(this._t0);
        }
    }

    /**
     * Câmera GTA: senta ATRÁS do avatar (no sentido do rumo) e olha um pouco à frente
     * dele. `target` = { x, z, heading, kind } (de EntityManager.getFollowTarget) ou
     * null quando o jogador não está em cena — nesse caso a câmera volta suavemente à
     * vista aérea padrão. Tudo suavizado no tempo (rumo, posição e alvo): quando o
     * avatar é encontrado, a câmera "mergulha" da vista aérea para trás dele.
     */
    updateFollowCamera(dt, target) {
        if (!this.followCam) return;
        // BLINDAGEM: um alvo com coordenada não-finita (transitório) envenenaria a
        // suavização acumulativa (_followPos vira NaN e nunca mais volta → tela preta).
        // Trata alvo inválido como "sem alvo" e, se o estado foi contaminado, re-assenta.
        if (target && !(Number.isFinite(target.x) && Number.isFinite(target.z) && Number.isFinite(target.heading))) target = null;
        if (!Number.isFinite(this._followPos.x)) this._followPos.copy(this.camera.position);
        if (!Number.isFinite(this._followAim.x)) this._followAim.copy(this._t0);

        let desiredPos, desiredAim, tau;
        if (!target) {
            // sem avatar (ainda não entrou / saiu) → retorna devagar p/ a vista aberta
            desiredPos = _v0.copy(this._t0).addScaledVector(this._camDir, this._camDistMax);
            desiredAim = _v1.copy(this._t0);
            tau = 0.8;
        } else {
            // enquadramento por tipo: carro anda mais rápido → um pouco mais longe/alto
            const car = target.kind === 'car';
            const dist = car ? 15 : 10.5;   // recuo atrás do avatar
            const height = car ? 8.0 : 6.2; // altura da câmera
            const lookY = car ? 1.8 : 1.7;  // altura do ponto visado
            const ahead = car ? 3.5 : 1.8;  // quão à frente do avatar a câmera mira
            // rumo suavizado (caminho curto) → gira junto sem tremer com o ruído do
            // heading a cada passo. heading θ ⇒ frente do avatar = (sinθ, cosθ) em (x,z).
            if (this._followYaw == null || !Number.isFinite(this._followYaw)) this._followYaw = target.heading;
            this._followYaw += shortAngle(this._followYaw, target.heading) * (1 - Math.exp(-dt / 0.35));
            const fx = Math.sin(this._followYaw), fz = Math.cos(this._followYaw);
            desiredPos = _v0.set(target.x - fx * dist, height, target.z - fz * dist);
            desiredAim = _v1.set(target.x + fx * ahead, lookY, target.z + fz * ahead);
            tau = 0.25;
        }

        const a = 1 - Math.exp(-dt / tau); // suavização de posição/alvo
        this._followPos.lerp(desiredPos, a);
        this._followAim.lerp(desiredAim, a);
        this.camera.position.copy(this._followPos);
        this.camera.lookAt(this._followAim);
    }

    /**
     * Ajusta a distância MÁX da câmera dinâmica (e empurra o fog junto) à PROPORÇÃO e
     * ALTURA ÚTIL da tela. O FOV do three é VERTICAL: numa tela estreita (retrato) o FOV
     * horizontal fica minúsculo, e numa paisagem de celular (baixa) as reservas de HUD
     * comem metade da altura — nos dois casos a câmera precisa RECUAR mais para enquadrar
     * os jogadores. Sem isso ela bate no teto de distância (calibrado p/ desktop) e fica
     * ESTÁTICA no mobile. Referência = desktop 16:10 → fator 1 → desktop INALTERADO.
     */
    _applyAspectFraming() {
        const aspect = this.camera.aspect || 1;
        const U = this._usefulArea();
        // capacidade angular ~ (aspect × meia-largura útil) na horizontal e (1 × meia-altura
        // útil) na vertical. REF = valores do desktop onde o enquadramento foi calibrado
        // (janela 1440×900 com as reservas atuais T=100/B=120/L=R=40 → hy=0.756).
        const REF_A = 1.6, REF_HX = 0.944, REF_HY = 0.756;
        const hDeficit = (REF_A * REF_HX) / (aspect * U.hx);
        const vDeficit = REF_HY / U.hy;
        // fator ≥ 1 (nunca aproxima além do desktop); teto 2.0 mantém a câmera dentro do
        // chão (plano 900²) e o fog suave. Desktop 16:9/16:10 → fator 1 (intacto).
        const k = Math.min(2.0, Math.max(1, hDeficit, vDeficit));
        this._camDistMax = this._baseDistMax * k;
        if (this._dynDist) this._dynDist = Math.min(this._dynDist, this._camDistMax);
        if (this.scene.fog) {
            this.scene.fog.near = this._baseFogNear;
            this.scene.fog.far = this._baseFogFar * k; // recuou → afasta o fog (k=1 = intacto)
        }
    }

    /** Liga/desliga o modo câmera dinâmica. */
    setDynamicCamera(on) {
        this.dynamicCam = !!on;
        this.controls.enabled = !this.dynamicCam; // sem orbitar/zoom manual no modo dinâmico
        if (this.dynamicCam) {
            this._dynTarget.copy(this.controls.target);
            this._dynDist = this.camera.position.distanceTo(this.controls.target);
            this._dynRot = 0;
        } else {
            this.controls.update(); // retoma o controle a partir da posição atual
        }
    }

    /**
     * Enquadra TODOS os jogadores: alvo → centro do retângulo que os contém e
     * distância calculada (fit do frustum) para caberem na tela com folga. Limita à
     * vista padrão (abertura/posição máxima). Quanto mais aproxima, mais gira (até
     * ~10°); ao abrir para a vista padrão a rotação zera. Tudo suavizado no tempo.
     * `focus` = { minX, maxX, minZ, maxZ, cx, cz, count }.
     */
    updateDynamicCamera(dt, focus) {
        if (!this.dynamicCam) return;
        const distMax = this._camDistMax;
        const distMin = distMax * 0.42; // zoom-in máximo
        const U = this._usefulArea(); // área útil (descontadas as reservas de tela)

        let desiredTarget, desiredDist, desiredRot;
        if (!focus || !focus.count) {
            desiredTarget = _v1.copy(this._t0); // sem jogadores → vista padrão
            desiredDist = distMax;
            desiredRot = 0;
        } else {
            // resolve alvo+distância+rotação com a GARANTIA de que TODOS os
            // jogadores cabem na área útil (ver _fitPlacement).
            const fit = this._fitPlacement(focus, distMin, distMax, U);
            desiredTarget = fit.aim;
            desiredDist = fit.d;
            desiredRot = fit.rot;
        }

        const a = 1 - Math.exp(-dt / 0.6); // suavização temporal
        this._dynTarget.lerp(desiredTarget, a);
        this._dynDist += (desiredDist - this._dynDist) * a;
        this._dynRot += (desiredRot - this._dynRot) * a;

        const dir = _v2.copy(this._camDir).applyAxisAngle(_UP, this._dynRot);
        this.controls.target.copy(this._dynTarget);
        this.camera.position.copy(this._dynTarget).addScaledVector(dir, this._dynDist);
        this.camera.lookAt(this._dynTarget);
    }

    /** Reservas de tela (px) p/ os elementos de apoio (TOAST de conquistas no topo,
     *  cards de score/missões + indicador online na base, logos) → ÁREA ÚTIL em NDC:
     *  centro (cx,cy) e meia-extensão (hx,hy). A câmera enquadra dentro dela. */
    _usefulArea() {
        const W = window.innerWidth || 1920;
        const H = window.innerHeight || 1080;
        const T = 100, // topo: toast de conquistas ocupa ~96px (card + avatar transbordando)
            B = 120,
            L = 40,
            R = 40; // base, esquerda, direita
        return {
            cx: (L - R) / W, // + = centro útil à direita
            cy: (B - T) / H, // + = centro útil mais p/ cima
            hx: Math.max(0.25, 1 - (L + R) / W), // meia-largura útil (NDC)
            hy: Math.max(0.25, 1 - (T + B) / H), // meia-altura útil (NDC)
        };
    }

    /**
     * Resolve DISTÂNCIA + ROTAÇÃO + ALVO da câmera dinâmica com a GARANTIA de que
     * TODOS os jogadores cabem na área útil da tela:
     *   A) fit clássico centrado no retângulo (zoom até caber, teto = vista padrão);
     *   B) CONTENÇÃO na configuração REAL: projeta os cantos com o alvo/rotação que a
     *      câmera vai usar de verdade (o fit A supõe alvo centrado — no meio do zoom o
     *      alvo interpolado desviava e cortava jogadores da borda) e, se algo ficar de
     *      fora, DESLOCA o alvo (pan de contenção) para trazer todos p/ dentro.
     * A VISTA PADRÃO é o TETO ABSOLUTO do zoom-out (decisão do usuário): se nem nela
     * o conjunto cabe (ex.: jogadores SAINDO da cidade pelas bordas), a câmera fica
     * na vista padrão (alvo padrão, sem perseguir) — quem está de saída pode sair do
     * quadro; o enquadramento nunca recua além da abertura máxima.
     */
    _fitPlacement(focus, distMin, distMax, U) {
        const MAX_ROT = (10 * Math.PI) / 180;
        const FILL = 0.9; // fit A ocupa ~90% da área útil (folga p/ rotação/suavização)
        const cam = this._fitCam;
        cam.fov = this.camera.fov;
        cam.aspect = this.camera.aspect;
        cam.up.set(0, 1, 0);
        const C = _v0.set(focus.cx, this._t0.y, focus.cz);
        const c = this._fitPts;
        c[0].set(focus.minX, 0, focus.minZ);
        c[1].set(focus.maxX, 0, focus.minZ);
        c[2].set(focus.minX, 0, focus.maxZ);
        c[3].set(focus.maxX, 0, focus.maxZ);
        c[4].set(focus.minX, 4, focus.minZ);
        c[5].set(focus.maxX, 4, focus.minZ);
        c[6].set(focus.minX, 4, focus.maxZ);
        c[7].set(focus.maxX, 4, focus.maxZ);

        // A) distância pelo fit clássico (centrado em C) — comportamento original
        let d = this._dynDist || distMax;
        for (let it = 0; it < 4; it++) {
            cam.position.copy(C).addScaledVector(this._camDir, d);
            cam.lookAt(C);
            cam.updateMatrixWorld();
            cam.updateProjectionMatrix();
            let mx = 0,
                my = 0;
            for (const p of c) {
                this._fitTmp.copy(p).project(cam);
                mx = Math.max(mx, Math.abs(this._fitTmp.x));
                my = Math.max(my, Math.abs(this._fitTmp.y));
            }
            const m = Math.max(mx / (FILL * U.hx), my / (FILL * U.hy));
            if (m < 1e-4) break;
            d = Math.max(distMin, Math.min(distMax, d * m));
        }

        // alvo/rotação da configuração REAL (mesmas fórmulas de sempre)
        const f = Math.max(0, Math.min(1, (distMax - d) / (distMax - distMin)));
        const rot = MAX_ROT * smoothStep(0.25, 1, f);
        const s = smoothStep(0, 0.3, f);
        const tanHalf = Math.tan((this.camera.fov * Math.PI) / 180 / 2);
        const dir = _v2.copy(this._camDir).applyAxisAngle(_UP, rot);
        const fwd = _v3.copy(dir).negate();
        const right = _v4.crossVectors(fwd, _UP).normalize();
        const camUp = _v5.crossVectors(right, fwd).normalize();
        const aim = this._fitAim.copy(this._t0).lerp(C, s);
        // PAN estético: joga os jogadores p/ o centro da ÁREA ÚTIL (some na vista aberta)
        aim.addScaledVector(right, -U.cx * (d * tanHalf) * this.camera.aspect * s);
        aim.addScaledVector(camUp, -U.cy * (d * tanHalf) * s);

        // B/C) GARANTIA: os 8 cantos DENTRO da janela útil na configuração real
        const M = 0.98; // janela quase cheia (folga mínima anti-borda)
        for (let it = 0; it < 3; it++) {
            cam.position.copy(aim).addScaledVector(dir, d);
            cam.lookAt(aim);
            cam.updateMatrixWorld();
            cam.updateProjectionMatrix();
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            for (const p of c) {
                this._fitTmp.copy(p).project(cam);
                if (this._fitTmp.x < minX) minX = this._fitTmp.x;
                if (this._fitTmp.x > maxX) maxX = this._fitTmp.x;
                if (this._fitTmp.y < minY) minY = this._fitTmp.y;
                if (this._fitTmp.y > maxY) maxY = this._fitTmp.y;
            }
            const loX = U.cx - M * U.hx, hiX = U.cx + M * U.hx;
            const loY = U.cy - M * U.hy, hiY = U.cy + M * U.hy;
            // o TAMANHO não cabe nem na vista padrão (jogadores saindo pelas bordas):
            // NÃO recua além dela — mantém o alvo como está (na abertura máxima o alvo
            // já é o padrão, s=0) e aceita que quem está de saída fique fora do quadro.
            const grow = Math.max((maxX - minX) / (hiX - loX), (maxY - minY) / (hiY - loY));
            if (grow > 1.001) break;
            // B) cabe mas está DESLOCADO → pan de contenção (traz todos p/ dentro)
            let sx = 0, sy = 0;
            if (maxX > hiX) sx = maxX - hiX;
            else if (minX < loX) sx = minX - loX;
            if (maxY > hiY) sy = maxY - hiY;
            else if (minY < loY) sy = minY - loY;
            if (Math.abs(sx) < 1e-4 && Math.abs(sy) < 1e-4) break; // todos dentro
            aim.addScaledVector(right, sx * d * tanHalf * this.camera.aspect);
            aim.addScaledVector(camUp, sy * d * tanHalf);
        }
        return { d, rot, aim };
    }

    _initLights() {
        this.hemi = new THREE.HemisphereLight(0xb6f0d2, 0x1a3324, 0.95);
        this.scene.add(this.hemi);

        this.amb = new THREE.AmbientLight(0x5c8070, 0.55);
        this.scene.add(this.amb);

        const key = new THREE.DirectionalLight(0xfff4e0, 2.0);
        key.position.set(-70, 130, 50);
        key.castShadow = true;
        // resolução/caixa/bias vêm do PERFIL DE BOOT (ver SHADOW): 1024² basta
        // para a vista aérea em 1080p; no telão 4K o mesmo texel ocupa o dobro de
        // pixels, e é o Ultra que dobra o mapa junto. Escolhido aqui e não em
        // _applyProfile porque o alvo do shadow map é alocado LAZY no primeiro
        // render — com ?q=ultra ele já nasce no tamanho certo, sem realocação.
        const sh = IS_ULTRA ? SHADOW.ultra : SHADOW.base;
        key.shadow.mapSize.set(sh.map, sh.map);
        key.shadow.camera.near = 40;
        key.shadow.camera.far = 460;
        const s = sh.s;
        key.shadow.camera.left = -s;
        key.shadow.camera.right = s;
        key.shadow.camera.top = s;
        key.shadow.camera.bottom = -s;
        key.shadow.bias = sh.bias;
        key.shadow.normalBias = sh.normalBias;
        this.scene.add(key);
        this.scene.add(key.target);
        this.key = key;

        // PREENCHIMENTO FRIO — era uma segunda DirectionalLight (0x2e7d5b, intensidade
        // 0.22–0.42, vinda de (60,50,-40)). Cada luz direcional é avaliada em TODO
        // pixel de TODO material iluminado da cena; ela é a única luz do jogo que não
        // projeta sombra nem define a direção principal — pagava caro por um efeito
        // difuso. Agora a contribuição dela é DOBRADA no ambiente (ver _ambientRGB):
        // some a variação direcional do preenchimento (o HemisphereLight já dá a
        // variação céu↔chão), mas a cor e o nível de luz da cena se mantêm.
        this.sunAzimuth = (3 * Math.PI) / 2; // orientação padrão do sol: Oeste
        this.setSun(13.75); // estado inicial: 13h45 (início da tarde)
    }

    /**
     * Posição do sol pelo horário do dia (0–24h): move a luz principal num arco
     * leste→oeste e ajusta cor, intensidade, céu e fog para simular o momento do
     * dia (amanhecer/dourado → meio-dia → pôr do sol → noite/luar).
     */
    setSun(hour) {
        this.sunHour = ((hour % 24) + 24) % 24;
        const phi = ((this.sunHour - 6) / 12) * Math.PI; // 6h→0, 12h→π/2, 18h→π
        const elev = Math.sin(phi); // 1 ao meio-dia, 0 no nascer/pôr, <0 à noite
        const eastWest = Math.cos(phi); // +leste → −oeste
        const R = 210;
        // posição horizontal base (arco leste→oeste) GIRADA pela ORIENTAÇÃO do sol
        // (Norte/Leste/Sul/Oeste) — define de qual direção o sol ilumina a cidade.
        const hx = eastWest * R * 0.82;
        const hz = -0.42 * R;
        const az = this.sunAzimuth || 0;
        const cs = Math.cos(az),
            sn = Math.sin(az);
        this.key.position.set(
            hx * cs - hz * sn,
            Math.max(elev, 0.06) * R, // mantém acima do horizonte (sombras válidas)
            hx * sn + hz * cs,
        );

        const day = smoothStep(0.02, 0.5, elev); // 0 baixo/noite → 1 sol alto
        const night = smoothStep(0.0, -0.26, elev); // 0 dia → 1 noite

        const warm = lerpHex(0xffae5e, 0xfff3df, day); // dourado → branco-quente
        this.key.color.setHex(lerpHex(warm, 0x7d97dc, night)); // → luar azulado
        this.key.intensity = 0.3 + 2.0 * day;

        this.hemi.intensity = 0.3 + 0.7 * day;
        // ambiente = ambiente-base + a antiga luz de preenchimento dobrada aqui
        this._ambientRGB(0.34 + 0.2 * day, 0.22 + 0.2 * day);

        const sky = lerpHex(0x050f0a, 0x0b2016, day);
        this.scene.background.setHex(sky);
        this.scene.fog.color.setHex(sky);

        // o sol MOVEU → reprojeta uma vez o shadow map (estático) p/ as sombras dos
        // prédios/árvores/postes acompanharem a nova posição do sol. Fora isso o mapa
        // segue congelado (performance) — só re-renderiza quando o sol muda.
        if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    }

    /**
     * Recompõe a AmbientLight somando o ambiente-base com a antiga luz de
     * PREENCHIMENTO (removida por custo — ver _initLights).
     *
     * Uma direcional entrega `cor × intensidade × dot(N,L)`; uma ambiente entrega
     * `cor × intensidade` uniforme. Substituir uma pela outra preservando o nível
     * de luz significa dobrar a direcional pelo seu dot(N,L) MÉDIO na cena —
     * FILL_FOLD. A luz vinha de (60,50,-40) normalizado ≈ (0.68, 0.57, -0.45); numa
     * superfície voltada para cima (chão e telhados, que dominam a área da tela)
     * isso dá 0.57, e nas fachadas verticais varia de 0 a 0.68. 0.5 é a média
     * ponderada pela área visível.
     *
     * A soma é feita em espaço LINEAR (é onde o three acumula a luz ambiente), com
     * intensidade fixa em 1 e todo o nível carregado na cor.
     */
    _ambientRGB(ambI, fillI) {
        const F = FILL_FOLD;
        this.amb.color.setRGB(
            _AMB_BASE.r * ambI + _FILL_BASE.r * fillI * F,
            _AMB_BASE.g * ambI + _FILL_BASE.g * fillI * F,
            _AMB_BASE.b * ambI + _FILL_BASE.b * fillI * F,
            THREE.LinearSRGBColorSpace,
        );
        this.amb.intensity = 1;
    }

    /** Orientação (azimute) do sol em radianos — gira de qual direção ele ilumina a
     *  cidade (Norte/Leste/Sul/Oeste). Reaplica a posição do sol e as sombras. */
    setSunAzimuth(rad) {
        this.sunAzimuth = rad;
        this.setSun(this.sunHour);
    }

    _initGround() {
        // MATERIAIS LAMBERT (eram Standard com roughness 0.95–1.0 e metalness 0).
        // O chão é o maior consumidor de pixels do jogo — cobre a tela inteira, é o
        // fundo de tudo. Numa superfície fosca e sem metal o especular do PBR não
        // aparece, mas o BRDF GGX + o espelhamento indireto rodam em CADA pixel da
        // tela. Lambert dá a mesma imagem por uma fração do custo; sombras recebidas,
        // fog e a textura procedural funcionam igual.
        // Base ampla (esconde além das bordas + recebe o fog)
        const base = new THREE.Mesh(new THREE.PlaneGeometry(900, 900), new THREE.MeshLambertMaterial({ color: COLORS.forest }));
        base.rotation.x = -Math.PI / 2;
        base.position.y = -0.1;
        base.receiveShadow = true;
        this.scene.add(base);

        // Chão da cidade (textura procedural)
        const tex = createCityTexture();
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(WORLD.width, WORLD.depth),
            new THREE.MeshLambertMaterial({ map: tex }),
        );
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        this.scene.add(ground);
        this.ground = ground;
    }

    _initPostFX() {
        this.composer = new EffectComposer(this.renderer);
        // ANTIALIASING via SMAA (pós-processo), em vez de MSAA: o MSAA exigia um alvo
        // HalfFloat MULTIAMOSTRADO com um "resolve" a cada frame — caro (e mudar 4↔2
        // amostras quase não alterava o custo, pois o peso era manter o alvo MSAA, não
        // o nº de amostras). O SMAA roda em alvos simples (single-sample), suaviza as
        // bordas com qualidade ~equivalente a 4x MSAA e custa bem menos.
        this.composer.renderTarget1.samples = 0;
        this.composer.renderTarget2.samples = 0;
        this.composer.addPass(new RenderPass(this.scene, this.camera));

        // PERFORMANCE: bloom em MEIA resolução. O brilho é borrado por natureza, então
        // a perda é imperceptível; e como é o passe mais pesado (pirâmide de blur com
        // vários níveis), processá-lo a 1/2 corta ~4x o seu custo.
        this.bloomScale = QUALITY.alta.bloom;
        this.bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth * this.bloomScale, window.innerHeight * this.bloomScale), BLOOM.strength, BLOOM.radius, BLOOM.threshold);
        this.composer.addPass(this.bloom);

        // Focus blur (cidade nítida + bordas borradas) FUNDIDO com o OutputPass
        // (tone mapping + sRGB): mesma imagem, um passe fullscreen a menos.
        this.focusBlur = new FocusOutputPass();
        this.focusBlur.uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
        this.composer.addPass(this.focusBlur);

        // SMAA por último: suaviza as bordas na imagem final (LDR/sRGB). Barato e dá
        // bordas bem lisas sem o custo do alvo MSAA HalfFloat.
        const pr = this.renderer.getPixelRatio();
        this.smaa = new SMAAPass(window.innerWidth * pr, window.innerHeight * pr);
        this.composer.addPass(this.smaa);

        // ALTERNATIVA barata ao SMAA: o SMAA são TRÊS passes de tela cheia
        // (detecção de bordas → pesos → mistura); o FXAA é UM. Os dois ficam no
        // composer e o modo de AA liga um ou outro — o EffectComposer pula passes
        // com `enabled=false` e reajusta sozinho quem desenha na tela.
        this.fxaa = new ShaderPass(FXAAShader);
        this.composer.addPass(this.fxaa);
        const aa = AA_MODES[this.aaMode];
        this.smaa.enabled = aa.smaa;
        this.fxaa.enabled = aa.fxaa;
        this._syncAAResolution();
    }

    /** Resolução em pixels REAIS para o FXAA (ele trabalha em texels). */
    _syncAAResolution() {
        if (!this.fxaa) return;
        const s = this.renderer.getSize(_size);
        const pr = this.renderer.getPixelRatio();
        this.fxaa.material.uniforms.resolution.value.set(1 / (s.x * pr), 1 / (s.y * pr));
    }

    /**
     * pixelRatio do quadro atual. Nos perfis de sempre é exatamente a expressão
     * de antes — `min(devicePixelRatio, maxPixelRatio)`; em Ultra (pr > 1) o
     * número vira ALVO e entram os dois tetos de segurança. A decisão é pura e
     * mora em qualityLevel.js (dirigida por scripts/ultraResHarness.mjs).
     */
    _targetPixelRatio() {
        return resolvePixelRatio({
            dpr: window.devicePixelRatio,
            cssW: window.innerWidth,
            cssH: window.innerHeight,
            maxPixelRatio: this.maxPixelRatio,
            maxTextureSize: this.renderer.capabilities.maxTextureSize || 8192,
        });
    }

    /**
     * Reconfigura o shadow map do sol (ver SHADOW). Só faz alguma coisa quando o
     * tamanho REALMENTE muda: `mapSize` só é lido na alocação do alvo, então
     * trocar exige descartar o mapa e pedir um re-render — o que só vale a pena
     * pagar de verdade. Custo por quadro continua zero (o mapa segue congelado).
     */
    _applyShadowProfile(cfg) {
        const k = this.key;
        if (!k || k.shadow.mapSize.x === cfg.map) return;
        k.shadow.map?.dispose();
        k.shadow.map = null; // força o three a realocar no próximo render
        k.shadow.mapSize.set(cfg.map, cfg.map);
        k.shadow.camera.left = -cfg.s;
        k.shadow.camera.right = cfg.s;
        k.shadow.camera.top = cfg.s;
        k.shadow.camera.bottom = -cfg.s;
        k.shadow.camera.updateProjectionMatrix();
        k.shadow.bias = cfg.bias;
        k.shadow.normalBias = cfg.normalBias;
        this.renderer.shadowMap.needsUpdate = true;
    }

    /**
     * NÍVEL DE QUALIDADE — mexe só no que domina o custo numa GPU integrada: a
     * RESOLUÇÃO de render (os ~8 passes fullscreen custam proporcionalmente a ela)
     * e o ANTIALIASING. Cor, sombra, brilho, nuvens e geometria não mudam — a cena
     * é a mesma, só mais ou menos nítida nas bordas.
     *
     *   ultra        pixelRatio ALVO 2.0 · bloom 0.5 · blur 2 anéis (supersampling)
     *   alta         pixelRatio 1.0 · bloom 0.5  · blur 2 anéis (referência, padrão)
     *   equilibrada  pixelRatio 0.9 · bloom 0.42 · blur 2 anéis (−19% de pixels)
     *   desempenho   pixelRatio 0.75 · bloom 0.32 · blur 1 anel  (−44% de pixels)
     * (o antialiasing é escolhido à parte — ver AA_MODES/setAAMode)
     *
     * ULTRA é o único que INVERTE a semântica do pr: nos outros ele é um teto
     * (nunca acima da tela), nele é um alvo (nunca abaixo da tela física, e acima
     * quando cabe no orçamento) — ver resolvePixelRatio em qualityLevel.js. A
     * parte dele que depende de TEXTURA (chão, fachadas, manchas de sombra, copa
     * dos arbustos) é decidida no boot e por isso o painel recarrega ao entrar ou
     * sair do Ultra; o que está aqui aplica na hora.
     */
    setQuality(level) {
        const q = QUALITY[level];
        this.quality = q ? level : "alta";
        this.autoRes.on = false;
        this._applyProfile(q || QUALITY.alta);
    }

    /**
     * O usuário JÁ escolheu um antialiasing à mão? (?aa= na URL ou painel →
     * localStorage). Se sim, os perfis NÃO mexem no AA — escolha explícita vence.
     */
    _aaEscolhidoPeloUsuario() {
        try {
            if (new URLSearchParams(window.location.search).get('aa')) return true;
            return localStorage.getItem('wab.aa') != null;
        } catch {
            return false;
        }
    }

    /**
     * RESOLUÇÃO AUTOMÁTICA: mantém a qualidade mais alta que o hardware aguenta.
     * Escada 1.0 → 0.9 → 0.85 → 0.75, cada degrau um PERFIL completo (resolução +
     * bloom + amostras do focus blur — ver AUTO_LADDER em autoRes.js).
     *
     * Como mede e por que (a 1ª versão tinha 3 defeitos, todos de medição):
     * o sinal é o tempo OCUPADO do quadro (busyMs), não o dt — o dt de um monitor
     * 60 Hz é 16,7 ms mesmo com a máquina folgada, e era por isso que a escada
     * nunca subia de volta. O fpsCap vira o ORÇAMENTO (com ?fps=30, ~33,3 ms) em
     * vez de "parecer lentidão"; sem teto vale targetFps (padrão 30). Aba oculta,
     * contexto perdido e pausas longas descartam a janela. Detalhes/limiares e o
     * porquê da histerese assimétrica: docblock de autoRes.js. Harness
     * determinístico: scripts/autoResHarness.mjs.
     *
     * @param {number} [targetFps] alvo SEM fpsCap (padrão 30 — PC fraco)
     */
    setAutoResolution(on, targetFps) {
        this.autoRes.enable(on, this.maxPixelRatio, this.clock.elapsedTime, targetFps);
    }

    /** Uma amostra por quadro renderizado; aplica o degrau quando a decisão mudar. */
    _tickAutoRes(gapMs, busyMs) {
        const next = this.autoRes.sample({
            busyMs,
            dtMs: gapMs,
            fpsCap: this.fpsCap,
            hidden: typeof document !== 'undefined' && document.hidden,
            contextLost: this._contextLost,
            nowS: this.clock.elapsedTime
        });
        // o degrau aplica o PERFIL INTEIRO (pr + bloom + rings) — a 1ª versão só
        // mexia no pixelRatio e o fundo da escada saía mais caro que o perfil
        // manual "desempenho"
        if (next != null) this._applyProfile(AUTO_LADDER[next]);
    }

    /** Aplica um perfil (resolução interna, escala do bloom, amostras do blur e,
     *  se o usuário nunca escolheu AA à mão, o antialiasing padrão do perfil).
     *  A troca de AA aqui só liga/desliga passes JÁ COMPILADOS do composer
     *  (SMAA/FXAA vivem os dois lá desde o boot) — nada recompila, nada realoca. */
    _applyProfile(q) {
        this.maxPixelRatio = q.pr;
        this.bloomScale = q.bloom;
        // pr > 1 é a assinatura do Ultra (alvo, não teto): leva junto a sombra de
        // resolução dobrada. Os 4 degraus da escada automática têm pr ≤ 1, então
        // entrar/sair da "Automática" nunca realoca o shadow map por engano.
        this._applyShadowProfile(q.pr > 1 ? SHADOW.ultra : SHADOW.base);
        if (this.focusBlur) this.focusBlur.uniforms.uRings.value = q.rings;
        if (q.aa && this.smaa && !this._aaEscolhidoPeloUsuario()) {
            const aa = AA_MODES[q.aa];
            if (aa) {
                this.aaMode = q.aa;
                this.smaa.enabled = aa.smaa;
                this.fxaa.enabled = aa.fxaa;
                this._syncAAResolution();
            }
        }
        this._onResize(); // reaplica pixelRatio + tamanho de todos os passes
    }

    /**
     * Troca o modo de antialiasing. SMAA/FXAA aplicam na hora; o MSAA do canvas
     * (`antialias`) é fixado na criação do contexto, então só vale no próximo
     * carregamento — o chamador é quem decide recarregar.
     * @returns {boolean} true se o modo exige recarregar para valer por inteiro
     */
    setAAMode(mode) {
        const aa = AA_MODES[mode];
        if (!aa) return false;
        this.aaMode = mode;
        try { localStorage.setItem("wab.aa", mode); } catch { /* sem storage */ }
        this.smaa.enabled = aa.smaa;
        this.fxaa.enabled = aa.fxaa;
        this._syncAAResolution();
        // o MSAA do canvas só muda recriando o contexto WebGL
        return aa.msaa !== AA_MODES[this._bootAA].msaa;
    }

    /** Direção horizontal (normalizada) em que as sombras caem — do sol para o alvo.
     *  Usada para alinhar as "blob shadows" dos avatares com as sombras dos prédios
     *  (acompanha o ajuste de sol). Reusa um objeto (sem alocar por frame). */
    shadowGroundDir() {
        const k = this.key;
        const dx = k.target.position.x - k.position.x;
        const dz = k.target.position.z - k.position.z;
        const m = Math.hypot(dx, dz) || 1;
        const o = this._shadowDir || (this._shadowDir = { x: 0, z: 1, len: 1 });
        o.x = dx / m;
        o.z = dz / m;
        // comprimento RELATIVO da sombra = cot(elevação do sol) = horizontal/altura:
        // sol alto → curta; sol baixo → longa (igual às sombras dos prédios).
        const horiz = Math.hypot(k.position.x, k.position.z);
        o.len = Math.max(0.5, Math.min(3.0, horiz / Math.max(20, k.position.y)));
        return o;
    }

    /**
     * Desenha a cena de sobreposição (rótulos) por cima da imagem já finalizada,
     * sem limpar o que o composer acabou de escrever. Chamada logo após cada
     * render — inclusive na captura de screenshot, senão os rótulos sumiriam do PNG.
     */
    _renderOverlay() {
        if (!this.overlay) return;
        // nada visível (ex.: "Exibir nomes" desligado) → nem chama o renderer
        let any = false;
        for (const c of this.overlay.children) if (c.visible) { any = true; break; }
        if (!any) return;
        const prevAutoClear = this.renderer.autoClear;
        this.renderer.autoClear = false;
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.overlay, this.camera);
        this.renderer.autoClear = prevAutoClear;
    }

    addUpdater(fn) {
        this.updaters.add(fn);
        return () => this.updaters.delete(fn);
    }

    add(obj) {
        this.scene.add(obj);
    }

    start() {
        if (!this._tick) {
            let acc = 0; // acumula o tempo entre quadros p/ aplicar o teto de FPS
            this._tick = () => {
                this._frame = requestAnimationFrame(this._tick);
                if (this._contextLost) return; // sem contexto GL → não tenta desenhar
                acc += this.clock.getDelta();
                // TETO DE FPS: só renderiza quando juntou ~1/fpsCap s. fpsCap=0 → sem teto.
                const minDt = this.fpsCap ? 1 / this.fpsCap - 0.002 : 0;
                if (acc < minDt) return;
                // O TETO DE 50 ms PROTEGE contra o salto da aba que voltou do congelamento,
                // mas o excedente NÃO pode ser jogado fora: com `acc = 0` o relógio das
                // animações só sabia ATRASAR em relação à parede, e nunca recuperava. Com
                // `?fps=15` o período do quadro (66,7 ms) já passa do teto e o clamp morde
                // TODO quadro — medido, 8 h de parede viravam 6 h de animação (25% perdidos):
                // o confete de 3 s durava 4 s e a cidade inteira andava devagar. Devolvendo o
                // resto ao acumulador, o atraso se paga nos quadros seguintes.
                const dt = Math.min(acc, 0.05);
                acc -= dt;
                // ...mas um acumulado ABSURDO (aba minimizada por horas) não deve ser
                // "pago": aí o certo é seguir do presente, que é o que o clamp faz.
                if (acc > 1) acc = 0;
                const t = this.clock.elapsedTime;

                // RESOLUÇÃO AUTOMÁTICA — duas medidas por quadro RENDERIZADO:
                // `gapMs` = parede entre este render e o anterior (direto do relógio,
                // imune à contabilidade do acumulador acima) → detector de slot
                // perdido/pausa; `frame0` abre a medição do tempo OCUPADO, fechada
                // depois do render + overlay. Ver autoRes.js.
                const frame0 = performance.now();
                const gapMs = this._lastRenderMs ? frame0 - this._lastRenderMs : 1000 / 60;
                this._lastRenderMs = frame0;

                // medidor de performance (Settings → PerfMeter). `this.perf` = null
                // quando desligado → nenhum custo extra no loop.
                const perf = this.perf;
                const cpu0 = perf ? performance.now() : 0;

                probes.begin(P.controles);
                this.controls.update();
                probes.end(P.controles);
                for (const fn of this.updaters) fn(dt, t);

                // Keep bloom strength steady: a scene-wide pulse also modulates
                // car lamps and reads as a failing light while they move.
                perf?.beginGpu();
                probes.begin(P.composer);
                this.composer.render();
                probes.end(P.composer);
                try {
                    probes.begin(P.overlay);
                    this._renderOverlay(); // rótulos por cima, sem pós-processamento
                    probes.end(P.overlay);
                } catch (err) {
                    // Um erro AQUI é traiçoeiro: o rAF do próximo quadro já foi agendado
                    // na primeira linha do tick, então a cena continua desenhando
                    // normalmente e o sintoma é só "os nomes sumiram e o medidor de
                    // performance travou em coletando…". Loga UMA vez e segue, para a
                    // causa aparecer no console em vez de virar adivinhação.
                    if (!this._overlayErr) {
                        this._overlayErr = true;
                        console.error('[overlay] falha ao desenhar os rótulos:', err);
                    }
                }
                if (perf) {
                    perf.endGpu();
                    perf.frame(performance.now() - cpu0);
                }
                // amostra DEPOIS do render/overlay — o busy tem que incluir o custo
                // de submissão do quadro inteiro, senão a escada mediria só a simulação
                this._tickAutoRes(gapMs, performance.now() - frame0);
                probes.endFrame(); // fecha o quadro das sondas por subsistema
            };
        }
        this._frame = requestAnimationFrame(this._tick);
    }

    /**
     * SCREENSHOT em ALTA RESOLUÇÃO (p/ impressão em lona/banner). Renderiza UM quadro
     * numa resolução arbitrária (4K/6K/8K…) redimensionando o renderer + todo o pipeline
     * de pós (bloom em meia-res, focus blur, SMAA) para o alvo, captura o PNG e RESTAURA
     * tudo ao estado da tela. Se a proporção alvo == proporção da tela, o resultado é
     * WYSIWYG (mesmo enquadramento, só com mais pixels). Devolve um Blob PNG — ou null
     * se a GPU não aguentar (contexto perdido / sem memória).
     * @param {number} width  largura alvo (px)
     * @param {number} height altura alvo (px)
     */
    async captureScreenshot(width, height) {
        const r = this.renderer;
        const c = this.composer;

        // TETO da GPU: nenhum alvo pode passar de maxTextureSize (~8k–16k). Se passar,
        // encolhe proporcionalmente (mantém a proporção pedida) em vez de falhar.
        const cap = r.capabilities.maxTextureSize || 8192;
        if (width > cap || height > cap) {
            const s = cap / Math.max(width, height);
            width = Math.floor(width * s);
            height = Math.floor(height * s);
        }
        width = Math.max(2, Math.round(width));
        height = Math.max(2, Math.round(height));

        // PAUSA o loop (evita renderizar em 4K a cada frame durante a captura e corridas
        // com o restauro). Retomamos no finally.
        const wasRunning = !!this._frame;
        if (this._frame) {
            cancelAnimationFrame(this._frame);
            this._frame = null;
        }

        // estado atual (tela) p/ restaurar depois
        const prev = new THREE.Vector2();
        r.getSize(prev);
        const prevPR = r.getPixelRatio();
        const prevAspect = this.camera.aspect;

        // aplica um tamanho ao renderer + composer + passes (bloom/focus). updateStyle=false:
        // NÃO mexe no CSS do canvas → nenhum "flash" visível na tela durante a captura.
        const applySize = (w, h, pr) => {
            r.setPixelRatio(pr);
            c.setPixelRatio(pr);
            r.setSize(w, h, false);
            c.setSize(w, h);
            this.camera.aspect = w / h;
            this.camera.updateProjectionMatrix();
            // composer.setSize devolveu o bloom p/ resolução cheia → volta p/ meia-res
            this.bloom?.setSize(Math.round(w * this.bloomScale), Math.round(h * this.bloomScale));
            this.focusBlur?.uniforms.uResolution.value.set(w, h);
            this._syncAAResolution();
        };

        let blob = null;
        try {
            applySize(width, height, 1); // alvo em pixels 1:1
            c.render();
            this._renderOverlay(); // sem isto os rotulos sumiriam do PNG
            // toBlob tira o "snapshot" dos pixels de forma síncrona (a codificação é que é
            // assíncrona) → captura o quadro em alta res mesmo sem preserveDrawingBuffer.
            blob = await new Promise((resolve) => r.domElement.toBlob(resolve, "image/png"));
        } catch (err) {
            console.error("[screenshot] falha ao renderizar em alta resolução", err);
        } finally {
            // RESTAURA a tela (tamanho/pixelRatio/proporção exatos) e re-renderiza 1 quadro
            applySize(prev.x, prev.y, prevPR);
            this.camera.aspect = prevAspect;
            this.camera.updateProjectionMatrix();
            c.render();
            this._renderOverlay(); // rotulos tambem no quadro restaurado
            if (wasRunning) {
                this.clock.getDelta(); // descarta o tempo parado (1º dt não fica gigante)
                this.start();
            }
        }
        return blob;
    }

    /**
     * Recuperação de PERDA DE CONTEXTO WebGL — essencial para um telão ligado 8h.
     * Sem isto, qualquer reset do driver/GPU (TDR, pressão de memória, troca de
     * HDMI/energia) deixa o canvas PRETO até um F5 manual. Aqui a gente:
     *   • `preventDefault()` no 'webglcontextlost' → sinaliza ao browser que QUEREMOS
     *     restaurar (sem isso ele nunca dispara 'webglcontextrestored');
     *   • para o loop e mostra um aviso enquanto está sem contexto;
     *   • no 'webglcontextrestored', re-renderiza o shadow map (está congelado) e
     *     reinicia o loop — o three recria os recursos de GPU sob demanda;
     *   • rede de segurança: se NÃO restaurar em 8 s, recarrega a página (o estado
     *     vive no servidor → recarregar re-sincroniza sem perder nada).
     */
    _initContextRecovery() {
        const canvas = this.renderer.domElement;
        canvas.addEventListener(
            "webglcontextlost",
            (e) => {
                e.preventDefault();
                this._contextLost = true;
                if (this._frame) cancelAnimationFrame(this._frame);
                this._showRecoverOverlay(true);
                // FUSÍVEL DO ULTRA. Ele é o único perfil que pode estourar a VRAM,
                // e com "ultra" persistido recarregar traria o mesmo estouro —
                // LOOP DE BOOT no telão do evento.
                //
                // Mas perder o contexto NÃO prova que o Ultra não cabe: um reset de
                // driver, a máquina suspensa e uma troca de HDMI derrubam o contexto
                // em qualquer perfil, e são o caso COMUM numa jornada de 8 h (é para
                // eles que existe todo o resto deste bloco). Queimar o fusível ali
                // rebaixaria o telão em silêncio — a sessão continua em Ultra, então
                // ninguém vê nada, e só o próximo reload, horas depois, volta em
                // "alta" sem explicação.
                //
                // Então o fusível queima só com evidência de que o problema é
                // capacidade, e não um soluço: quando o contexto NÃO voltou (o
                // reload de segurança abaixo disparou), ou quando cai uma SEGUNDA
                // vez na mesma sessão (perde/restaura em ciclo = a cena não cabe).
                // Um kiosk fixado em ?q=ultra ignora tudo isto: a URL vence o
                // storage — é decisão de quem montou a URL (ver docs §1.1).
                this._lostCount = (this._lostCount || 0) + 1;
                if (this._lostCount > 1) this._rebaixarUltra();
                clearTimeout(this._lostTimer);
                this._lostTimer = setTimeout(() => {
                    this._rebaixarUltra(); // não voltou em 8 s: aí sim é capacidade
                    location.reload();
                }, 8000);
            },
            false,
        );
        canvas.addEventListener(
            "webglcontextrestored",
            () => {
                clearTimeout(this._lostTimer);
                this._contextLost = false;
                this.renderer.shadowMap.needsUpdate = true; // mapa congelado → re-renderiza 1x
                this._showRecoverOverlay(false);
                this.clock.getDelta(); // descarta o tempo parado p/ o 1º dt não ser gigante
                this.start(); // retoma o loop (recursos de GPU recriados sob demanda)
            },
            false,
        );
    }

    /** Queima o fusível do Ultra: o próximo carregamento vem em "alta". No-op em
     *  qualquer outro perfil (e num kiosk com ?q=ultra, que a URL sobrepõe). */
    _rebaixarUltra() {
        if (this.quality !== 'ultra') return;
        try { localStorage.setItem('wab.quality', 'alta'); } catch { /* sem storage */ }
    }

    /** Aviso simples enquanto o vídeo é recuperado (e some ao restaurar). */
    _showRecoverOverlay(show) {
        if (show) {
            if (this._recoverEl) return;
            const el = document.createElement("div");
            el.textContent = "Recuperando vídeo…";
            el.style.cssText = "position:fixed;inset:0;display:flex;align-items:center;justify-content:center;" + "background:#06120b;color:#3dff86;font:700 22px Inter,sans-serif;z-index:9999";
            document.body.appendChild(el);
            this._recoverEl = el;
        } else if (this._recoverEl) {
            this._recoverEl.remove();
            this._recoverEl = null;
        }
    }

    _onResize() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this._applyAspectFraming(); // re-ajusta a vista aberta à nova proporção (girar o cel)
        const pr = this._targetPixelRatio();
        this.renderer.setPixelRatio(pr);
        this.composer.setPixelRatio(pr);
        this.renderer.setSize(w, h);
        this.composer.setSize(w, h);
        // mantém o bloom em meia resolução (o composer.setSize o teria voltado a cheio)
        this.bloom?.setSize(Math.round(w * this.bloomScale), Math.round(h * this.bloomScale));
        this.focusBlur?.uniforms.uResolution.value.set(w, h);
        this._syncAAResolution();
    }
}

/**
 * Modo de antialiasing escolhido: `?aa=` na URL (útil para comparar na TV sem
 * mexer em configuração) > localStorage > padrão.
 */
function readAAMode() {
    try {
        const q = new URLSearchParams(location.search).get("aa");
        if (q && AA_MODES[q]) return q;
    } catch { /* sem location */ }
    try {
        const v = localStorage.getItem("wab.aa");
        if (v && AA_MODES[v]) return v;
    } catch { /* sem storage */ }
    return AA_DEFAULT;
}

/* ----------------------- utilidades de interpolação ----------------------- */

function smoothStep(e0, e1, x) {
    const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
}

/** Menor diferença angular de `from` a `to` (rad), no intervalo (-π, π].
 *
 * Reduz de uma vez (resto), não subtraindo 2π num laço. O `_followYaw` da câmera ENROLA:
 * ele acompanha o alvo somando diferenças, então um avatar que dá voltas na cidade o leva
 * a milhares de radianos ao longo do evento. Com o laço, cada quadro pagava uma iteração
 * por volta já acumulada — na oitava hora de um telão, ~960 por quadro, e piorando. */
function shortAngle(from, to) {
    const TAU = Math.PI * 2;
    let d = (to - from) % TAU;
    if (d > Math.PI) d -= TAU;
    else if (d < -Math.PI) d += TAU;
    return d;
}

/** Interpola dois inteiros 0xRRGGBB e devolve o hex resultante. */
function lerpHex(a, b, k) {
    const ar = (a >> 16) & 255,
        ag = (a >> 8) & 255,
        ab = a & 255;
    const br = (b >> 16) & 255,
        bg = (b >> 8) & 255,
        bb = b & 255;
    const r = Math.round(ar + (br - ar) * k);
    const g = Math.round(ag + (bg - ag) * k);
    const bl = Math.round(ab + (bb - ab) * k);
    return (r << 16) | (g << 8) | bl;
}
